import { FC } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ListState, { hasListState } from '../../components/ListState';
import { colors, fontWeights, radii, shadows } from '../../theme/tokens';
import RowActions, { DocumentAction } from './RowActions';
import {
  DOCUMENTS_ERROR_TEXT,
  RowResolvers,
  emptyDocumentsText,
} from './DocumentsTable';
import { HomeDocument } from './types';

// The grid view of the Home and Résultats, to the official card of the
// "Flux 2" revision (DESIGN.md, "Revisión desktop"): dense cards with the
// thumbnail on top, the file name in orange, archiveur and service, the
// "Emis le" / "Archivé" rows and a "•••" that expands in place into
// edit / download / delete. The card itself (thumbnail and title) opens
// the record: the artboard draws no "Voir" action.
//
// Thumbnail — what there is today and what will come:
//   - `thumbnail_url`: the backend-generated thumbnail (M1 in ROADMAP.md).
//     It does not exist yet; as soon as the listing brings it, it is used first.
//   - `image_url`: the file path, which only serves as a thumbnail when it is
//     an image (the browser does not paint a PDF in an <img>). The search
//     listing does not return it today (verified with curl on 23/08: the rows
//     of `/search` carry the same fields as `/documents`, without
//     `file_path` nor `image_url`), so for now the case does not arise.
//   - Without either of the two: a placeholder with the PDF icon and the title.
//     It is what every document sees today, because the assembly always produces
//     a PDF and there is no rasterisation (M1).
//
// An image that fails to load falls back to the placeholder, so that a removed
// file does not leave a broken gap.

type Props = {
  documents: HomeDocument[];
  isLoading: boolean;
  isError: boolean;
  errorText?: string;
  resolvers: RowResolvers;
  onAction: (action: DocumentAction, document: HomeDocument) => void;
  onRetry: () => void;
};

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp)$/i;

export const thumbnailSource = (document: HomeDocument): string | null => {
  if (document.thumbnail_url) return absolute(document.thumbnail_url);
  if (document.image_url && IMAGE_EXTENSIONS.test(document.image_url)) {
    return absolute(document.image_url);
  }
  return null;
};

// The file paths arrive relative to the API ("/api/image_documents/…").
const absolute = (url: string): string =>
  url.startsWith('/') ? `${process.env.REACT_APP_API_URL}${url}` : url;

const Thumbnail: FC<{ document: HomeDocument }> = ({ document }) => {
  const source = thumbnailSource(document);

  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '4 / 3',
        backgroundColor: colors.surface,
        borderBottom: `1px solid ${colors.border}`,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {source ? (
        <Box
          component="img"
          src={source}
          alt=""
          loading="lazy"
          onError={(event) => {
            // Without the image the placeholder underneath remains.
            (event.currentTarget as HTMLImageElement).style.display = 'none';
          }}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
          }}
        />
      ) : null}
      {/* The "Flux 2" placeholder is a bare image block; the orange title
          right under the thumbnail already names the document. */}
      <PictureAsPdfOutlinedIcon
        aria-hidden
        sx={{ fontSize: 40, color: colors.accent }}
      />
    </Box>
  );
};

const DocumentGrid: FC<Props> = ({
  documents,
  isLoading,
  isError,
  errorText = DOCUMENTS_ERROR_TEXT,
  resolvers,
  onAction,
  onRetry,
}) => {
  const state = {
    isLoading,
    isError,
    isEmpty: documents.length === 0,
    errorText,
    emptyText: emptyDocumentsText(true),
    onRetry,
  };

  if (hasListState(state)) {
    return <ListState {...state} />;
  }

  const dateRow = (label: string, value: string) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: '0.6875rem', color: colors.muted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.6875rem', color: colors.textBase }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box
      component="ul"
      aria-label="Documents"
      data-testid="document-grid"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'grid',
        gap: 1.5,
        // "Flux 2" draws 8 dense columns at 1280; auto-fill keeps that
        // density and degrades with the width.
        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      }}
    >
      {documents.map((document) => (
        <Box
          component="li"
          key={document.id}
          data-testid="document-card"
          sx={{
            backgroundColor: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: `${radii.card}px`,
            boxShadow: shadows.card,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': { borderColor: colors.accent },
          }}
        >
          {/* The card opens the record: thumbnail and title link to it. */}
          <Link
            to={`/document/${document.id}`}
            tabIndex={-1}
            aria-hidden
            style={{ display: 'block' }}
          >
            <Thumbnail document={document} />
          </Link>

          <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <Link
                to={`/document/${document.id}`}
                style={{
                  color: colors.accent,
                  fontWeight: fontWeights.medium,
                  textDecoration: 'none',
                  overflowWrap: 'anywhere',
                  fontSize: '0.8125rem',
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {document.title}
              </Link>
              {document.confidential && (
                <Tooltip title="Confidentiel">
                  <LockOutlinedIcon
                    fontSize="small"
                    sx={{ color: colors.muted }}
                    aria-label="Confidentiel"
                  />
                </Tooltip>
              )}
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: colors.textBase }}>
                {resolvers.archiverName(document)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: colors.textBase }}>
                {resolvers.serviceName(document)}
              </Typography>
            </Box>

            <Box sx={{ mt: 'auto', pt: 0.5 }}>
              {dateRow('Emis le', resolvers.formatDate(document.issuing_date))}
              {dateRow('Archivé', resolvers.formatDate(document.archiving_date))}
            </Box>

            <RowActions
              document={document}
              canWrite={resolvers.canWrite(document)}
              variant="grid"
              onAction={onAction}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DocumentGrid;
